// Libs
import path from "path";

/**
 * SLUGS
 *
 * - Single piece of a URL
 *
 * e.g. "reports" in "/documents/reports"
 */
export const SLUGS = {
  ABOUT: "about",
  ACCOUNT: "account",
  ADD_TEAM_MEMBER: "add-team-member",
  ANALYTICS: "analytics",
  CHANGE_PASSWORD: "change-password",
  CLAIMS: "claims",
  CLINICAL: "clinical",
  CONDITIONS: "conditions",
  DENTAL: "dental",
  DETAILS: "details",
  DOCUMENTS: "documents",
  EDIT_TEAM_MEMBER: "edit-team-member",
  ENROLLMENT_SUMMARY: "enrollment-summary",
  HIGH_COST_CLAIMANTS: "high-cost-claimants",
  INSIGHTS: "insights",
  LANDING: "landing",
  LOGIN: "login",
  LOGOUT: "logout",
  MEDICAL: "medical",
  PEOPLE: "people",
  PEOPLE_EMERGENCY_ADD: "emergency-add",
  PARTNERS: "partners",
  POSING: "posing",
  REPORTS: "reports",
  RESOURCES: "resources",
  RX: "rx",
  SEARCH: "search",
  SERVICE: "service",
  SERVICES: "services",
  VISION: "vision",
  WELCOME: "welcome"
};

/**
 * PATHS
 *
 * - Used to link to different parts of the app
 * - Composed of SLUG(S)
 * - Not parsed by the router (so don't include ":myParam" in these)
 *
 * e.g. "/accounts/reset-password"
 */
export const PATHS = {
  get BASE() {
    return "/";
  },

  get ABOUT() {
    return path.join(this.BASE, SLUGS.ABOUT);
  },

  get ACCOUNT() {
    return path.join(this.BASE, SLUGS.ACCOUNT);
  },
  get ACCOUNT_ADD_TEAM_MEMBER() {
    return path.join(this.ACCOUNT, SLUGS.ADD_TEAM_MEMBER);
  },
  get ACCOUNT_EDIT_TEAM_MEMBER() {
    return path.join(this.ACCOUNT, SLUGS.EDIT_TEAM_MEMBER);
  },
  get ACCOUNT_CHANGE_PASSWORD() {
    return path.join(this.ACCOUNT, SLUGS.CHANGE_PASSWORD);
  },

  get ANALYTICS() {
    return path.join(this.BASE, SLUGS.ANALYTICS);
  },

  get CLAIMS() {
    return path.join(this.BASE, SLUGS.CLAIMS);
  },
  get CLAIMS_DENTAL() {
    return path.join(this.CLAIMS, SLUGS.DENTAL);
  },
  get CLAIMS_DENTAL_SERVICE() {
    return path.join(this.CLAIMS_DENTAL, SLUGS.SERVICE);
  },
  get CLAIMS_ENROLLMENT_SUMMARY() {
    return path.join(this.CLAIMS, SLUGS.ENROLLMENT_SUMMARY);
  },
  get CLAIMS_HIGH_COST() {
    return path.join(this.CLAIMS, SLUGS.HIGH_COST_CLAIMANTS);
  },
  get CLAIMS_MEDICAL() {
    return path.join(this.CLAIMS, SLUGS.MEDICAL);
  },
  get CLAIMS_MEDICAL_SERVICE() {
    return path.join(this.CLAIMS_MEDICAL, SLUGS.SERVICE);
  },
  get CLAIMS_MEDICAL_CLINICAL() {
    return path.join(this.CLAIMS_MEDICAL, SLUGS.CLINICAL);
  },
  get CLAIMS_MEDICAL_CLINICAL_DETAIL() {
    return path.join(this.CLAIMS_MEDICAL_CLINICAL, SLUGS.DETAILS);
  },
  get CLAIMS_RX() {
    return path.join(this.CLAIMS, SLUGS.RX);
  },
  get CLAIMS_VISION() {
    return path.join(this.CLAIMS, SLUGS.VISION);
  },
  get CLAIMS_PARTNERS() {
    return path.join(this.CLAIMS, SLUGS.PARTNERS);
  },
  get CLAIMS_PARTNERS_DETAIL() {
    return path.join(this.CLAIMS_PARTNERS, SLUGS.DETAILS);
  },
  get DOCUMENTS() {
    return path.join(this.BASE, SLUGS.DOCUMENTS);
  },

  get LANDING() {
    return path.join(this.BASE, SLUGS.LANDING);
  },
  get LOGIN() {
    return path.join(this.BASE, SLUGS.LOGIN);
  },
  get LOGOUT() {
    return path.join(this.BASE, SLUGS.LOGOUT);
  },

  get PEOPLE() {
    return path.join(this.BASE, SLUGS.PEOPLE);
  },
  get PEOPLE_SEARCH() {
    return path.join(this.PEOPLE, SLUGS.SEARCH);
  },
  get PEOPLE_DETAILS() {
    // Adds /:id in actual route but path is the same
    return path.join(this.PEOPLE_SEARCH);
  },
  get PEOPLE_DETAILS_CLAIMS() {
    return path.join(this.PEOPLE_SEARCH, SLUGS.CLAIMS);
  },
  get PEOPLE_EMERGENCY_ADD() {
    return path.join(this.PEOPLE, SLUGS.PEOPLE_EMERGENCY_ADD);
  },

  get POSING() {
    return path.join(this.BASE, SLUGS.POSING);
  },

  get WELCOME() {
    return path.join(this.BASE, SLUGS.WELCOME);
  }
};

/**
 * ROUTE PARAMS
 *
 * - Used in routes to identify a dynamic part of the URL
 *
 * e.g. "category" in "/documents/:category"
 */
export const ROUTE_PARAMS = {
  DOC_CATEGORY: "docCategory",
  DOC_TYPE: "docType",
  UUID: "uuid",
  SERVICE: "service",
  CLINICAL_ID: "clinicalId",
  PARTNER_ID: "partnerId",
  TOKEN: "token"
};

export const QUERY_PARAMS = {
  CLAIMS_MONTH: "claimsMonth",
  CLAIMS_YEAR: "claimsYear",
  CLAIMS_DATE_RANGE: "claimsDateRange",
  CLAIMS_RUNOUT: "claimsRunout",
  CLAIMS_RUNOUT_LABEL: "claimsRunoutLabel",
  CLAIMS_PLANS: "claimsPlans",
  CLAIMS_SUBSIDIARIES: "claimsSubsidiaries",
  CLAIMS_SORT_DIRECTION: "claimsSortDirection",
  CLAIMS_SORT_KEY: "claimsSortKey",
  CLINICAL_ID: "clinicalId",
  ENV: "env",
  PARTNER_ID: "partnerId",
  PEOPLE_MONTH: "peopleMonth",
  PEOPLE_YEAR: "peopleYear",
  PORT: "port",
  POSEUID: "poseuid",
  SUBDOMAIN: "subdomain",
  TERM: "term",
  TRACK_LOGOUT: "trackLogout"
};

/**
 * ROUTES
 *
 * - Parsed by the router to build route hierarchy
 * - Composed of PATHS and ROUTE_PARAMS
 *
 * e.g. "/people/search/:term"
 */
export const ROUTES = {
  BASE: PATHS.BASE,

  ABOUT: PATHS.ABOUT,

  ACCOUNT: PATHS.ACCOUNT,
  ACCOUNT_ADD_TEAM_MEMBER: PATHS.ACCOUNT_ADD_TEAM_MEMBER,
  ACCOUNT_EDIT_TEAM_MEMBER: path.join(PATHS.ACCOUNT_EDIT_TEAM_MEMBER, `:${ROUTE_PARAMS.UUID}`),
  ACCOUNT_CHANGE_PASSWORD: PATHS.ACCOUNT_CHANGE_PASSWORD,

  ANALYTICS: PATHS.ANALYTICS,
  CLAIMS: PATHS.CLAIMS,
  CLAIMS_DENTAL: PATHS.CLAIMS_DENTAL,
  CLAIMS_DENTAL_SERVICE: path.join(PATHS.CLAIMS_DENTAL_SERVICE, `:${ROUTE_PARAMS.SERVICE}`),
  CLAIMS_ENROLLMENT_SUMMARY: PATHS.CLAIMS_ENROLLMENT_SUMMARY,
  CLAIMS_HIGH_COST: PATHS.CLAIMS_HIGH_COST,
  CLAIMS_MEDICAL: PATHS.CLAIMS_MEDICAL,
  CLAIMS_MEDICAL_SERVICE: PATHS.CLAIMS_MEDICAL_SERVICE,
  CLAIMS_MEDICAL_SERVICE_DETAIL: path.join(
    PATHS.CLAIMS_MEDICAL_SERVICE,
    `:${ROUTE_PARAMS.SERVICE}`
  ),
  CLAIMS_MEDICAL_CLINICAL: PATHS.CLAIMS_MEDICAL_CLINICAL,
  CLAIMS_MEDICAL_CLINICAL_DETAIL: path.join(
    PATHS.CLAIMS_MEDICAL_CLINICAL_DETAIL,
    `:${ROUTE_PARAMS.CLINICAL_ID}`
  ),
  CLAIMS_MEDICAL_CLINICAL_INSIGHTS: path.join(
    PATHS.CLAIMS_MEDICAL_CLINICAL_DETAIL,
    `:${ROUTE_PARAMS.CLINICAL_ID}`,
    SLUGS.INSIGHTS
  ),
  CLAIMS_MEDICAL_CLINICAL_CONDITIONS: path.join(
    PATHS.CLAIMS_MEDICAL_CLINICAL_DETAIL,
    `:${ROUTE_PARAMS.CLINICAL_ID}`,
    SLUGS.CONDITIONS
  ),
  CLAIMS_MEDICAL_CLINICAL_SERVICES: path.join(
    PATHS.CLAIMS_MEDICAL_CLINICAL_DETAIL,
    `:${ROUTE_PARAMS.CLINICAL_ID}`,
    SLUGS.SERVICES
  ),
  CLAIMS_RX: PATHS.CLAIMS_RX,
  CLAIMS_VISION: PATHS.CLAIMS_VISION,
  CLAIMS_PARTNERS: PATHS.CLAIMS_PARTNERS,
  CLAIMS_PARTNERS_DETAIL: path.join(PATHS.CLAIMS_PARTNERS_DETAIL, `:${ROUTE_PARAMS.PARTNER_ID}`),

  DOCUMENTS: path.join(
    PATHS.DOCUMENTS,
    `:${ROUTE_PARAMS.DOC_CATEGORY}?`,
    `:${ROUTE_PARAMS.DOC_TYPE}?`
  ),

  LANDING: PATHS.LANDING,
  LOGIN: PATHS.LOGIN,
  LOGOUT: PATHS.LOGOUT,

  PEOPLE: PATHS.PEOPLE,
  PEOPLE_DETAILS: path.join(PATHS.PEOPLE_DETAILS, `:${ROUTE_PARAMS.UUID}`),
  PEOPLE_DETAILS_CLAIMS: path.join(PATHS.PEOPLE_DETAILS_CLAIMS, `:${ROUTE_PARAMS.UUID}`),
  PEOPLE_EMERGENCY_ADD: path.join(PATHS.PEOPLE, SLUGS.PEOPLE_EMERGENCY_ADD),
  PEOPLE_OVERVIEW: PATHS.PEOPLE,
  PEOPLE_SEARCH: PATHS.PEOPLE_SEARCH,

  POSING: path.join(PATHS.POSING),

  WELCOME: PATHS.WELCOME
};

/**
 * ROUTE CONFIGS
 *
 * - Shared route configs that define all the properties of a route.
 * - Order here matters: exact routes should be before non-exact routes
 *
 * e.g.
 * {
 *   exact: true,
 *   path: ROUTES.MY_PATH,
 *   strict: true
 * }
 */
export const ROUTE_CONFIGS = {
  BASE: {
    path: ROUTES.BASE,
    exact: true,
    strict: true
  },
  LANDING: {
    path: ROUTES.LANDING,
    exact: true,
    strict: true
  },
  LOGIN: {
    path: ROUTES.LOGIN,
    exact: true,
    strict: true
  },
  LOGOUT: {
    path: ROUTES.LOGOUT,
    exact: true,
    strict: true
  },
  WELCOME: {
    path: ROUTES.WELCOME,
    exact: true,
    strict: true
  },

  ABOUT: {
    path: ROUTES.ABOUT,
    exact: true,
    strict: true
  },

  ACCOUNT: {
    path: ROUTES.ACCOUNT,
    exact: true,
    strict: true
  },
  ANALYTICS: {
    path: ROUTES.ANALYTICS,
    exact: true,
    strict: true
  },
  CLAIMS: {
    path: ROUTES.CLAIMS,
    exact: true,
    strict: true
  },
  DOCUMENTS: {
    path: ROUTES.DOCUMENTS,
    exact: true,
    strict: true
  },
  PEOPLE: {
    path: ROUTES.PEOPLE,
    exact: true,
    strict: true
  },

  ACCOUNT_ADD_TEAM_MEMBER: {
    path: ROUTES.ACCOUNT_ADD_TEAM_MEMBER,
    exact: true,
    strict: true
  },
  ACCOUNT_EDIT_TEAM_MEMBER: {
    path: ROUTES.ACCOUNT_EDIT_TEAM_MEMBER,
    exact: true,
    strict: true
  },
  ACCOUNT_CHANGE_PASSWORD: {
    path: ROUTES.ACCOUNT_CHANGE_PASSWORD,
    exact: true,
    strict: true
  },
  CLAIMS_DENTAL_SERVICE: {
    path: ROUTES.CLAIMS_DENTAL_SERVICE,
    exact: true,
    strict: true
  },
  CLAIMS_ENROLLMENT_SUMMARY: {
    path: ROUTES.CLAIMS_ENROLLMENT_SUMMARY,
    exact: true,
    strict: true
  },
  CLAIMS_HIGH_COST: {
    path: ROUTES.CLAIMS_HIGH_COST,
    exact: false,
    strict: true
  },
  CLAIMS_MEDICAL: {
    path: ROUTES.CLAIMS_MEDICAL,
    exact: false,
    strict: true
  },
  CLAIMS_MEDICAL_SERVICE: {
    path: ROUTES.CLAIMS_MEDICAL_SERVICE,
    exact: false,
    strict: true
  },
  CLAIMS_MEDICAL_SERVICE_DETAIL: {
    path: ROUTES.CLAIMS_MEDICAL_SERVICE_DETAIL,
    exact: true,
    strict: true
  },
  CLAIMS_MEDICAL_CLINICAL: {
    path: ROUTES.CLAIMS_MEDICAL_CLINICAL,
    exact: false,
    strict: true
  },
  CLAIMS_MEDICAL_CLINICAL_DETAIL: {
    path: ROUTES.CLAIMS_MEDICAL_CLINICAL_DETAIL,
    exact: false,
    strict: true
  },
  CLAIMS_MEDICAL_CLINICAL_INSIGHTS: {
    path: ROUTES.CLAIMS_MEDICAL_CLINICAL_INSIGHTS,
    exact: true,
    strict: true
  },
  CLAIMS_MEDICAL_CLINICAL_CONDITIONS: {
    path: ROUTES.CLAIMS_MEDICAL_CLINICAL_CONDITIONS,
    exact: true,
    strict: true
  },
  CLAIMS_MEDICAL_CLINICAL_SERVICES: {
    path: ROUTES.CLAIMS_MEDICAL_CLINICAL_SERVICES,
    exact: true,
    strict: true
  },
  CLAIMS_DENTAL: {
    path: ROUTES.CLAIMS_DENTAL,
    exact: false,
    strict: true
  },
  CLAIMS_RX: {
    path: ROUTES.CLAIMS_RX,
    exact: false,
    strict: true
  },
  CLAIMS_VISION: {
    path: ROUTES.CLAIMS_VISION,
    exact: false,
    strict: true
  },
  CLAIMS_PARTNERS: {
    path: ROUTES.CLAIMS_PARTNERS,
    exact: false,
    strict: true
  },
  CLAIMS_PARTNERS_DETAIL: {
    path: ROUTES.CLAIMS_PARTNERS_DETAIL,
    exact: false,
    strict: true
  },
  PEOPLE_DETAILS: {
    path: ROUTES.PEOPLE_DETAILS,
    exact: true,
    strict: true
  },
  PEOPLE_DETAILS_CLAIMS: {
    path: ROUTES.PEOPLE_DETAILS_CLAIMS,
    exact: false,
    strict: true
  },
  PEOPLE_EMERGENCY_ADD: {
    path: ROUTES.PEOPLE_EMERGENCY_ADD,
    exact: true,
    strict: true
  },
  PEOPLE_SEARCH: {
    path: ROUTES.PEOPLE_SEARCH,
    exact: true,
    strict: true
  },
  PEOPLE_OVERVIEW: {
    path: ROUTES.PEOPLE_OVERVIEW,
    exact: true,
    strict: true
  }
};

/**
 * ROUTE TO PAGE TITLE MAP
 *
 * - Mapping of routes to their corresponding page titles.
 *
 */
export const ROUTE_TO_PAGE_TITLE_MAP = {
  [ROUTES.ABOUT]: "About Your Data",
  [ROUTES.ACCOUNT_ADD_TEAM_MEMBER]: "Add Team Member",
  [ROUTES.ACCOUNT_CHANGE_PASSWORD]: "Change Password",
  [ROUTES.ACCOUNT_EDIT_TEAM_MEMBER]: "Edit Team Member",
  [ROUTES.ACCOUNT]: "Account Overview",
  [ROUTES.BASE]: "Home",
  [ROUTES.CLAIMS]: "Claims",
  [ROUTES.CLAIMS_DENTAL_SERVICE]: "Dental Claims Service",
  [ROUTES.CLAIMS_DENTAL]: "Dental Claims",
  [ROUTES.CLAIMS_ENROLLMENT_SUMMARY]: "Enrollment Summary",
  [ROUTES.CLAIMS_HIGH_COST]: "High Cost Claimants",
  [ROUTES.CLAIMS_MEDICAL]: "Claims Medical",
  [ROUTES.CLAIMS_MEDICAL_SERVICE]: "Medical Claims Service",
  [ROUTES.CLAIMS_MEDICAL_SERVICE_DETAIL]: "Medical Claims Service Detail",
  [ROUTES.CLAIMS_MEDICAL_CLINICAL]: "Medical Claims Clinical",
  [ROUTES.CLAIMS_MEDICAL_CLINICAL_INSIGHTS]: "Medical Claims Clinical Insights",
  [ROUTES.CLAIMS_MEDICAL_CLINICAL_CONDITIONS]: "Medical Claims Clinical Categories",
  [ROUTES.CLAIMS_MEDICAL_CLINICAL_SERVICES]: "Medical Claims Clinical Services",
  [ROUTES.CLAIMS_RX]: "RX Claims",
  [ROUTES.CLAIMS_VISION]: "Vision Claims",
  [ROUTES.CLAIMS_PARTNERS]: "Program Partners",
  // [ROUTES.CLAIMS_PARTNER_DETAILS]: "Program Partner Details",
  [ROUTES.CLAIMS]: "Claims",
  [ROUTES.DOCUMENTS]: "Documents",
  // TODO: Add dynamically generated documents sub-pages to this map
  [ROUTES.PEOPLE_EMERGENCY_ADD]: "Emergency Add",
  [ROUTES.LANDING]: "Landing",
  [ROUTES.LOGOUT]: "Logout",
  [ROUTES.PEOPLE_DETAILS]: "People Details",
  [ROUTES.PEOPLE_OVERVIEW]: "People Overview",
  [ROUTES.PEOPLE_SEARCH]: "People Search",
  [ROUTES.WELCOME]: "Welcome"
};
